import type { Logger } from '@opentelemetry/api-logs';

type LogMethod = 'log' | 'info' | 'warn' | 'error' | 'debug';
const severityLevels: Record<LogMethod, 'DEBUG' | 'INFO' | 'WARN' | 'ERROR'> = {
  log: 'INFO',
  info: 'INFO',
  warn: 'WARN',
  error: 'ERROR',
  debug: 'DEBUG',
};

/**
 * Patches the global console methods to emit log messages using an OpenTelemetry-compatible logger.
 * This ensures log messages are instrumented and sent to the provided logger.
 * The patch is idempotent and will not apply multiple times.
 *
 * @param {Logger} logger - The OpenTelemetry-compatible logger instance that will handle the emitted log messages.
 * @return {void} This function does not return a value.
 */
export function patchConsoleWithOtel(logger: Logger): void {
  const globalContext = globalThis;
  if (globalContext.__otelConsolePatched) return;
  globalContext.__otelConsolePatched = true;

  const originalMethods: Record<LogMethod, (...args: unknown[]) => void> = {
    log: console.log.bind(console),
    info: (console.info ?? console.log).bind(console),
    warn: (console.warn ?? console.log).bind(console),
    error: (console.error ?? console.log).bind(console),
    debug: (console.debug ?? console.log).bind(console),
  };

  (Object.keys(originalMethods) as LogMethod[]).forEach((methodName) => {
    console[methodName] = (...args: unknown[]) => {
      originalMethods[methodName](...args);
      const logBody = args.map((argument) => formatArgument(argument)).join(' ');
      logger.emit({ severityText: severityLevels[methodName], body: logBody });
    };
  });
}

/**
 * Formats an argument for logging based on its type
 * @param argument - The argument to format
 * @returns Formatted string representation of the argument
 */
function formatArgument(argument: unknown): string {
  if (argument instanceof Error) {
    return `${argument.name}: ${argument.message}`;
  }

  if (typeof argument === 'string') {
    return argument;
  }

  if (typeof argument === 'number' || typeof argument === 'boolean') {
    return String(argument);
  }

  return serializeToJson(argument);
}

/**
 * Safely serializes a value to JSON string.
 * Falls back to String() if JSON serialization fails.
 *
 * @param {unknown} value - The value to serialize
 * @return {string} The serialized value
 */
function serializeToJson(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
